/**
 * OrganizationForm - Edit organization details
 *
 * Form for creating/updating organization information with progressive disclosure.
 * Uses Catalyst form components.
 */

import { useState } from 'react';

import { Button } from '@/components/ui/catalyst/button';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from '@/components/ui/catalyst/fieldset';
import { Input } from '@/components/ui/catalyst/input';
import { Select } from '@/components/ui/catalyst/select';
import { Textarea } from '@/components/ui/catalyst/textarea';
import type {
  CreateOrganization,
  ReadOrganization,
  UpdateOrganization,
} from '@/services/api/partiesService';

interface OrganizationFormProps {
  organization?: ReadOrganization;
  matterId: string;
  onSubmit: (data: CreateOrganization | UpdateOrganization) => void;
  onCancel: () => void;
  onDelete?: (id: string) => void;
}

export const OrganizationForm = ({
  organization,
  matterId,
  onSubmit,
  onCancel,
  onDelete,
}: OrganizationFormProps) => {
  const [formData, setFormData] = useState<
    Partial<CreateOrganization | UpdateOrganization>
  >({
    legal_name: organization?.legal_name || '',
    dba_name: organization?.dba_name || '',
    organization_type: organization?.organization_type || 'corporation',
    tax_id: organization?.tax_id || '',
    address_line1: organization?.address_line1 || '',
    address_line2: organization?.address_line2 || '',
    city: organization?.city || '',
    state: organization?.state || '',
    zip: organization?.zip || '',
    identity_status: organization?.identity_status || 'unknown',
    involvement_description: organization?.involvement_description || '',
    short_description: organization?.short_description || '',
    is_claimant: organization?.is_claimant || false,
  });

  const [showAdvanced, setShowAdvanced] = useState(false);

  const handleChange = (field: string, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = organization
      ? ({ ...formData, id: organization.id } as UpdateOrganization)
      : ({ ...formData, matter_id: matterId } as CreateOrganization);
    onSubmit(payload);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Fieldset>
        <FieldGroup>
          {/* Name fields */}
          <Field>
            <Label>Legal Name</Label>
            <Input
              type="text"
              value={formData.legal_name || ''}
              onChange={(e) => handleChange('legal_name', e.target.value)}
            />
          </Field>

          <Field>
            <Label>DBA Name</Label>
            <Input
              type="text"
              value={formData.dba_name || ''}
              onChange={(e) => handleChange('dba_name', e.target.value)}
              placeholder="Doing Business As"
            />
          </Field>

          {/* Organization type */}
          <div className="grid grid-cols-2 gap-4">
            <Field>
              <Label>Organization Type</Label>
              <Select
                value={formData.organization_type || 'corporation'}
                onChange={(e) =>
                  handleChange('organization_type', e.target.value)
                }
              >
                <option value="corporation">Corporation</option>
                <option value="llc">LLC</option>
                <option value="llp">LLP</option>
                <option value="partnership">Partnership</option>
                <option value="trust">Trust</option>
                <option value="other">Other</option>
              </Select>
            </Field>

            <Field>
              <Label>Tax ID (EIN)</Label>
              <Input
                type="text"
                value={formData.tax_id || ''}
                onChange={(e) => handleChange('tax_id', e.target.value)}
                placeholder="XX-XXXXXXX"
              />
            </Field>
          </div>

          {/* Involvement description */}
          <Field>
            <Label>Involvement</Label>
            <Textarea
              value={formData.involvement_description || ''}
              onChange={(e) =>
                handleChange('involvement_description', e.target.value)
              }
              placeholder="Brief description of how this organization was involved in the incident"
              rows={2}
            />
          </Field>

          {/* Advanced fields - progressive disclosure */}
          {showAdvanced && (
            <>
              <Field>
                <Label>Address Line 1</Label>
                <Input
                  type="text"
                  value={formData.address_line1 || ''}
                  onChange={(e) =>
                    handleChange('address_line1', e.target.value)
                  }
                />
              </Field>

              <Field>
                <Label>Address Line 2</Label>
                <Input
                  type="text"
                  value={formData.address_line2 || ''}
                  onChange={(e) =>
                    handleChange('address_line2', e.target.value)
                  }
                />
              </Field>

              <div className="grid grid-cols-3 gap-4">
                <Field>
                  <Label>City</Label>
                  <Input
                    type="text"
                    value={formData.city || ''}
                    onChange={(e) => handleChange('city', e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>State</Label>
                  <Input
                    type="text"
                    value={formData.state || ''}
                    onChange={(e) => handleChange('state', e.target.value)}
                    maxLength={2}
                  />
                </Field>
                <Field>
                  <Label>ZIP</Label>
                  <Input
                    type="text"
                    value={formData.zip || ''}
                    onChange={(e) => handleChange('zip', e.target.value)}
                  />
                </Field>
              </div>

              <Field>
                <Label>Identity Status</Label>
                <Select
                  value={formData.identity_status || 'unknown'}
                  onChange={(e) =>
                    handleChange('identity_status', e.target.value)
                  }
                >
                  <option value="unknown">Unknown</option>
                  <option value="described">Described</option>
                  <option value="named">Named</option>
                </Select>
              </Field>

              <Field>
                <Label>Short Description</Label>
                <Input
                  type="text"
                  value={formData.short_description || ''}
                  onChange={(e) =>
                    handleChange('short_description', e.target.value)
                  }
                  placeholder="Brief identifier if name unknown"
                />
              </Field>
            </>
          )}

          {/* Toggle advanced fields */}
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            {showAdvanced ? 'Hide' : 'Show'} additional fields
          </button>
        </FieldGroup>
      </Fieldset>

      {/* Actions */}
      <div className="flex items-center justify-between">
        {organization && onDelete && (
          <Button
            type="button"
            onClick={() => onDelete(organization.id)}
            color="red"
          >
            Delete
          </Button>
        )}
        <div className="flex justify-end gap-3 ml-auto">
          <Button type="button" onClick={onCancel} plain>
            Cancel
          </Button>
          <Button type="submit">
            {organization ? 'Update' : 'Create'} Organization
          </Button>
        </div>
      </div>
    </form>
  );
};
